import { OrderJson } from "@smeetz/orders";
import { Channel } from "@smeetz/order-common";
import type {
  CompanyInfo,
  FiscalInformation,
  Locale,
  ReceiptCountry,
  ReceiptInput,
} from "@smeetz/receipt";
import { IBookingReceipt, IReceiptGroupInfo } from "@/interfaces/receipts";
import DeviceModule from "@/store/modules/device";
import UserModule from "@/store/modules/user";
import CustomersModule from "@/store/modules/customers";
import POSConfigModule from "@/store/modules/pos-config";
import i18n from "@/lang";

/** Countries for which the POS holds per-sale fiscal signature info. */
const FISCAL_COUNTRIES: ReceiptCountry[] = ["FR", "AT", "DE", "IT", "ES", "HR"];

/** Map the active POS i18n locale → package Locale (default 'en'). */
function mapLocale(): Locale {
  const raw = (i18n.global as any)?.locale;
  const l = String(raw?.value ?? raw ?? "en").toLowerCase();
  const supported: Locale[] = ["en", "fr", "de", "it", "es", "hr", "nl"];
  return (supported as string[]).includes(l) ? (l as Locale) : "en";
}

export function buildOrganizer(
  g: IReceiptGroupInfo,
  country: string,
  receiptInfo?: IBookingReceipt
): CompanyInfo {
  // Header name (legacy HeaderNamePartial rule): DE → registered company name,
  // ES → public display name, else trading name.
  const headerName = receiptInfo?.germanyAdditionalInfo
    ? g.companyName || g.name
    : receiptInfo?.spainAdditionalInfo
    ? g.displayName || g.name
    : g.name;
  return {
    name: headerName || "",
    address: (g.street ?? g.address) || "",
    city: g.street !== undefined ? g.city : undefined,
    zip: g.street !== undefined ? g.zip : undefined,
    country,
    phone: g.phone,
    vatNumber: g.vatNumber,
    registrationNum: g.registrationNum,
    exoNumber: g.exoNumber,
    // France legal identifiers
    siret: g.siret,
    siren: g.siren,
    naf: g.nafCode,
    rcs: g.rcs,
    socialCapital: g.socialCapital,
    legalForm: g.legalForm,
    // Spain CIF
    nif: g.cif || g.vatId,
  };
}

/**
 * Per-sale fiscal blob from the already-computed `receiptInfo.*AdditionalInfo`.
 * NOL scope: France (compliance-common-v2). The other fiscal countries still
 * print via the legacy template on the order flow (their signing is not
 * migrated yet), so no fiscal blob is built for them here.
 */
export function buildFiscalInformation(
  receiptInfo: IBookingReceipt,
  country: string
): { fiscal?: FiscalInformation; isReprint: boolean } {
  if (
    !(FISCAL_COUNTRIES as string[]).includes(country) ||
    !receiptInfo.isSignatureRequired
  ) {
    return { fiscal: undefined, isReprint: false };
  }

  if (country !== "FR" || !receiptInfo.franceAdditionalInfo) {
    return { fiscal: undefined, isReprint: false };
  }

  const f = receiptInfo.franceAdditionalInfo;
  const isReprint = !!f.isReprint;
  // `documentTypexOperation` is "${documentType} / ${operationType}"; the package
  // rebuilds that string itself, so split it back to the two atomic parts.
  const [documentType, ...operationParts] = (
    f.documentTypexOperation || ""
  ).split(" / ");
  const operationType = operationParts.join(" / ") || undefined;

  const sec = f.signature ?? "";
  const swVersion = f.swVersion ?? "";
  const printCount = f.printCount ?? "";
  const fiscalNumber = f.fiscalNumber ?? "";
  const originalFiscalNumber = f.originalFiscalNumber ?? "";
  const reprintTimestamp = f.reprintTimestamp ?? "";

  const fiscal: FiscalInformation = {
    country: "FR",
    fiscalNumber,
    signature: sec, // FR's signing chain IS the sec code
    // The EFR-signed sale time is the fiscal time control; the package dates the receipt from it.
    signedAt: f.efrSaleTimestamp || undefined,
    // The package checks the NF525 fields of a signed receipt; an unsigned one prints its notice.
    isSigned: !!receiptInfo.isSigned,
    deviceUid: f.posId || DeviceModule.hardwareLocation?.deviceUid || "",
    countrySpecific: {
      france: {
        sec,
        swVersion,
        printCount,
        documentType,
        operationType,
        // Reprint-only data; the timestamps are the EFR's own strings, printed
        // verbatim by the package (no timezone math — FIS-652/L3-8341).
        reprint: isReprint
          ? {
              originalFiscalNumber,
              reprintTimestamp,
              originalSignature: f.originalSignature,
              originalPrintTimestamp: `${receiptInfo.date} ${receiptInfo.hour}`,
              swVersionOriginal: f.swVersionOriginal,
              originalSellerName: f.originalSellerName,
            }
          : undefined,
      },
    },
  };
  return { fiscal, isReprint };
}

/**
 * Assemble the @smeetz/receipt ReceiptInput for an ORDER (new order logic). The order is
 * passed as is: the package picks the session to print, the receipt date and the
 * references. The host only adds what the order cannot know: organizer, fiscal blob
 * and print context.
 */
export function buildOrderReceiptInput(
  order: OrderJson,
  receiptInfo: IBookingReceipt
): ReceiptInput {
  // ISO alpha-2 from the group config — receiptInfo.groupInfo.country is a
  // display NAME ("France"), not a code, and the package keys its
  // country-conditional rendering (VAT letters, legal rows) on the code.
  const country = (POSConfigModule.getGroupCountryCode || "").toUpperCase();
  const { fiscal, isReprint } = receiptInfo.isBill
    ? { fiscal: undefined, isReprint: false }
    : buildFiscalInformation(receiptInfo, country);

  const { firstName, lastName } = UserModule.user;
  const customerId = CustomersModule.getSelectedCustomerId;

  return {
    organizer: buildOrganizer(receiptInfo.groupInfo, country, receiptInfo),
    order,
    fiscal,
    meta: {
      locale: mapLocale(),
      operator: receiptInfo.franceAdditionalInfo?.sellerName
        ? { name: receiptInfo.franceAdditionalInfo.sellerName }
        : { firstName, lastName },
      customer: customerId
        ? {
            id: String(customerId),
            firstName: CustomersModule.getSelectedCustomerFirstName,
            lastName: CustomersModule.getSelectedCustomerLastName,
          }
        : undefined,
      channel: Channel.SMEETZ_POS,
      isReprint: isReprint || !!receiptInfo.isReceiptCopy || undefined,
      isBill: receiptInfo.isBill || undefined,
    },
  };
}
